import React, { useState } from 'react';
import { Layers } from 'lucide-react';
import { DentonLifePre1850Section } from './dentons/DentonLifePre1850Section';
import { WhatHappenedFirstGenSection } from './dentons/WhatHappenedFirstGenSection';
import { FrederickEstherSection } from './dentons/FrederickEstherSection';
import { TommyDentonSection } from './dentons/TommyDentonSection';
import { HarryDentonSection } from './dentons/HarryDentonSection';
import { IsabelDentonSection } from './dentons/IsabelDentonSection';
import { JohnFrederickSection } from './dentons/JohnFrederickSection';
import { AnnieGibbSection } from './dentons/AnnieGibbSection';
import { IsaacEphraimSection } from './dentons/IsaacEphraimSection';

export interface DentonPartItem {
  id: string;
  partNumber: number;
  title: string;
  subtitle: string;
}

export const DENTON_PARTS: DentonPartItem[] = [
  { id: 'part1', partNumber: 1, title: 'Denton life in England pre-1850', subtitle: 'Great Barton, Rushbrooke Hall, 1845–1849 Timeline & Emigration' },
  { id: 'part2', partNumber: 2, title: 'What happened to the first generation Dentons', subtitle: 'Arrival in Canterbury 1851, Fever of 1851 & Early Christchurch' },
  { id: 'part3', partNumber: 3, title: 'Frederick Robert & Esther Denton', subtitle: 'Early Canterbury Settlers & Family Line' },
  { id: 'part4', partNumber: 4, title: 'John Frederick & Eliza DENTON', subtitle: 'Pioneering generation & Canterbury land records' },
  { id: 'part5', partNumber: 5, title: 'The Story of Young Annie Gibb', subtitle: 'Heathfield, Greta Valley, Burnie Gibb Letters & Uncle Ted Roberts' },
  { id: 'part6', partNumber: 6, title: 'Isaac Ephraim Denton', subtitle: 'Canterbury farming & lineage' },
  { id: 'part7', partNumber: 7, title: 'Tommy Denton', subtitle: 'Gallipoli, the Egyptian Carpet & Southbridge farming' },
  { id: 'part8', partNumber: 8, title: 'Harry Denton WW2', subtitle: 'Lancaster Bomber Command & Distinguished Flying Cross' },
  { id: 'part9', partNumber: 9, title: 'Isabel & Warren Denton', subtitle: 'Women’s Land Service & North Canterbury family' },
];

export const DentonsSection: React.FC = () => {
  const [activePartId, setActivePartId] = useState<string>('part1');

  const currentPart = DENTON_PARTS.find(p => p.id === activePartId) || DENTON_PARTS[0];

  const handleSelectPart = (partId: string) => {
    if (partId === 'hub') {
      setActivePartId('all');
    } else if (partId === 'denton-first-gen') {
      setActivePartId('part2');
    } else {
      setActivePartId(partId);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-16 font-sans text-[#2c221e]">
      
      {/* 9-Part Series Navigation Bar */}
      <nav aria-label="Denton Series 9-Part Navigation" className="bg-[#f3ede0] p-3 sm:p-4 rounded-2xl border-2 border-[#d8ceb9] shadow-xs">
        <div className="flex items-center justify-between gap-2 pb-2.5 mb-2.5 border-b border-[#d8ceb9] text-xs font-serif text-[#7a5b3a]">
          <div className="flex items-center gap-1.5 font-bold uppercase tracking-wider">
            <Layers className="w-4 h-4 text-[#7a5b3a]" />
            <span>The Dentons: 9-Part Historical Series</span>
          </div>
          <button
            onClick={() => setActivePartId(activePartId === 'all' ? 'part1' : 'all')}
            className="text-[11px] font-bold text-[#7a5b3a] hover:underline bg-[#e8dec9] px-2.5 py-1 rounded-lg transition"
          >
            {activePartId === 'all' ? 'View Single Part' : 'View All Chapters'}
          </button>
        </div>

        {/* Horizontal Scrollable Part Tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-thin">
          {DENTON_PARTS.map((part) => {
            const isSelected = activePartId === part.id;
            return (
              <button
                key={part.id}
                onClick={() => {
                  setActivePartId(part.id);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className={`whitespace-nowrap px-3 py-1.5 rounded-xl text-xs font-serif font-bold transition flex items-center gap-1.5 shrink-0 border ${
                  isSelected
                    ? 'bg-[#7a5b3a] text-white border-[#7a5b3a] shadow-xs'
                    : 'bg-[#fbf9f4] text-[#4a3525] border-[#d8ceb9] hover:bg-[#e8dec9]'
                }`}
              >
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${isSelected ? 'bg-amber-200 text-[#2c221e]' : 'bg-[#e8dec9] text-[#7a5b3a]'}`}>
                  {part.partNumber}
                </span>
                <span>{part.title}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* RENDER ACTIVE PART */}
      {activePartId === 'part1' && (
        <DentonLifePre1850Section onSelectPart={handleSelectPart} />
      )}

      {activePartId === 'part2' && (
        <WhatHappenedFirstGenSection onSelectPart={handleSelectPart} />
      )}

      {activePartId === 'part3' && (
        <FrederickEstherSection onSelectPart={handleSelectPart} />
      )}

      {activePartId === 'part4' && (
        <JohnFrederickSection onSelectPart={handleSelectPart} />
      )}

      {activePartId === 'part5' && (
        <AnnieGibbSection onSelectPart={handleSelectPart} />
      )}

      {activePartId === 'part6' && (
        <IsaacEphraimSection onSelectPart={handleSelectPart} />
      )}

      {activePartId === 'part7' && (
        <TommyDentonSection onSelectPart={handleSelectPart} />
      )}

      {activePartId === 'part8' && (
        <HarryDentonSection onSelectPart={handleSelectPart} />
      )}

      {activePartId === 'part9' && (
        <IsabelDentonSection onSelectPart={handleSelectPart} />
      )}

      {/* ALL PARTS VIEW */}
      {activePartId === 'all' && (
        <div className="space-y-12">
          <DentonLifePre1850Section onSelectPart={handleSelectPart} />
          
          <hr className="border-t-4 border-dashed border-[#d8ceb9]" />

          <WhatHappenedFirstGenSection onSelectPart={handleSelectPart} />
          
          <hr className="border-t-4 border-dashed border-[#d8ceb9]" />

          <FrederickEstherSection onSelectPart={handleSelectPart} />

          <hr className="border-t-4 border-dashed border-[#d8ceb9]" />

          <JohnFrederickSection onSelectPart={handleSelectPart} />

          <hr className="border-t-4 border-dashed border-[#d8ceb9]" />

          <AnnieGibbSection onSelectPart={handleSelectPart} />

          <hr className="border-t-4 border-dashed border-[#d8ceb9]" />

          <IsaacEphraimSection onSelectPart={handleSelectPart} />

          <hr className="border-t-4 border-dashed border-[#d8ceb9]" />

          <TommyDentonSection onSelectPart={handleSelectPart} />

          <hr className="border-t-4 border-dashed border-[#d8ceb9]" />

          <HarryDentonSection onSelectPart={handleSelectPart} />

          <hr className="border-t-4 border-dashed border-[#d8ceb9]" />

          <IsabelDentonSection onSelectPart={handleSelectPart} />
        </div>
      )}

    </div>
  );
};

